// Core type definitions for the entire application

export * from './beatpack';

export enum UserRole {
  FREE = 'free',
  STARTER = 'starter',
  PRO = 'pro',
  PREMIUM = 'premium',
}

export enum FileType {
  BEAT = 'beat',
  SAMPLE = 'sample',
  LOOP = 'loop',
  ONESHOT = 'oneshot',
  PACK = 'pack',
}

export enum FileStatus {
  WIP = 'wip',               // Work in Progress (aktuell in Bearbeitung)
  DRAFT = 'draft',           // Draft (noch nicht fertig)
  TAGGED = 'tagged',         // Has voice tag (Preview)
  UNTAGGED = 'untagged',     // Clean version (for sale)
  READY = 'ready',           // Ready for sale
  SOLD = 'sold',             // Sold (Exclusive)
  ARCHIVED = 'archived',     // Archived
}

export enum AudioFormat {
  MP3_128 = 'mp3_128',
  MP3_320 = 'mp3_320',
  WAV = 'wav',
  FLAC = 'flac',
  AIFF = 'aiff',
}

export enum TagType {
  CATEGORY = 'category',
  GENRE = 'genre',
  BPM = 'bpm',
  KEY = 'key',
  MOOD = 'mood',
  INSTRUMENT = 'instrument',
  CUSTOM = 'custom',
}

export interface Tag {
  id: string;
  label: string;
  type: TagType;
  color?: string;
}

export interface AudioFile {
  id: string;
  name: string;
  originalName: string;
  type: FileType;
  size: number;
  duration: number;
  format: AudioFormat;
  url?: string; // Legacy: Direct URL (deprecated, use cdn_url instead)
  cdn_url?: string; // CDN URL from CloudFront/S3 (primary)
  path?: string; // Storage path (legacy, for Supabase Storage)
  s3_key?: string; // S3/Storage key from database (for signed URLs)
  userId?: string;
  previewUrl?: string;
  waveformUrl?: string;
  thumbnailUrl?: string;
  metadata?: any; // For BPM, key, etc. from DB
  
  // Metadata
  bpm?: number;
  key?: string;
  tags: Tag[];
  
  // Timestamps
  createdAt?: string;
  uploadedAt: string;
  updatedAt: string;
  lastAccessedAt?: string;
  
  // Organization
  folderId?: string;
  isStarred: boolean;
  isTrashed: boolean;
  status?: FileStatus;
  
  // Simple Version Control for Producers
  versionType?: 'tagged' | 'clean' | 'wav' | 'mp3' | 'stems' | 'master';
  groupId?: string;  // Files with same groupId are related versions
  isMainVersion?: boolean;  // Manual override: force this file to be the main version
  versionNote?: string | null; // User-authored notes per version
  versionTags?: string[]; // Custom tags for version management (master, v1, v2, completed, wip, etc.)
  
  // Sharing & Rights
  isPublic: boolean;
  shareToken?: string;
  downloads: number;
  plays: number;
  
  // File Share Info (for UI indicators)
  isShared?: boolean;
  shareInfo?: {
    shareToken?: string;
    hasPassword?: boolean;
    expiresAt?: string;
  };

  // BeatStars & collaborator (for share pages)
  beatstarsSellUrl?: string | null;
  beatstarsPrice?: string | null;
  collaborator?: string | null;
}

export interface Folder {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  files: AudioFile[];
  subfolders: Folder[];
  color?: string;
  icon?: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  plan: UserPlan;
  storageUsed: number;
  storageLimit: number;
  storageAddons?: number; // Additional storage in bytes
  features?: string[];
  createdAt?: string;
  isSuspended?: boolean; // Account suspension status
  billingInterval?: 'monthly' | 'yearly';
  subscriptionStatus?: string | null;
  subscriptionRenewsAt?: string | null;
}

export enum UserPlan {
  FREE = 'free',
  BEDROOM = 'bedroom',
  PRO = 'pro',
  STUDIO = 'studio',
}

export interface UploadProgress {
  fileId: string;
  fileName: string;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error' | 'paused' | 'retrying';
  error?: string;
}

export interface Beatpack {
  id: string;
  name: string;
  description?: string;
  coverUrl?: string;
  files: AudioFile[];
  price?: number;
  previewUrl?: string;
  downloads: number;
  createdAt: string;
  isPublic: boolean;
}

export interface Analytics {
  totalFiles: number;
  totalBeats: number;
  totalSamples: number;
  totalLoops: number;
  storageUsed: number;
  totalPlays: number;
  totalDownloads: number;
  topFiles: AudioFile[];
  recentActivity: Activity[];
  filesThisMonth: number; // NEW: Echte Daten für "This Month"
  lastUploadDate: Date | null; // NEW: Echte Daten für "Last Upload"
  versionsThisPeriod: number; // Anzahl Versionen im aktuellen Billing-Zeitraum
  youtubeUploadsThisPeriod: number; // Anzahl YouTube-Uploads im aktuellen Billing-Zeitraum
  activePublicBeatPacks: number; // Anzahl aktiver öffentlicher Beat Packs
}

export interface Activity {
  id: string;
  type: 'upload' | 'download' | 'share' | 'delete' | 'move' | 'tag' | 'play';
  fileId?: string;
  fileName?: string;
  description: string;
  timestamp: Date;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  action: () => void;
  col: number;
  row: number;
  danger?: boolean;
}

export interface ChangelogItem {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  badge?: string;
  date: Date;
}


